import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import supabase from '../utils/supabaseClient';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import './ExerciseSolver.css';

const getLocalSubmissionsKey = (userId) => `mathbox_local_submissions_${userId}`;

const appendLocalSubmission = (userId, submission) => {
    if (!userId) return;
    try {
        const key = getLocalSubmissionsKey(userId);
        const existingRaw = localStorage.getItem(key);
        const existing = existingRaw ? JSON.parse(existingRaw) : [];
        const next = [
            ...existing,
            {
                ...submission,
                created_at: submission.created_at || new Date().toISOString(),
            },
        ];
        localStorage.setItem(key, JSON.stringify(next));
    } catch {
        // ignore localStorage failures
    }
};

const renderLatex = (text) => {
    if (!text) return '';
    // Replace display LaTeX $$...$$
    let result = text.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
        try {
            return katex.renderToString(latex.trim(), { displayMode: true, throwOnError: false });
        } catch {
            return `$$${latex}$$`;
        }
    });
    // Replace inline LaTeX $...$
    result = result.replace(/\$([^$\n]+?)\$/g, (_, latex) => {
        try {
            return katex.renderToString(latex.trim(), { displayMode: false, throwOnError: false });
        } catch {
            return `$${latex}$`;
        }
    });
    // Convert newlines to <br>
    result = result.replace(/\n/g, '<br/>');
    return result;
};

const ExerciseSolver = () => {
    const { isDarkTheme } = useTheme();
    const { user } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [exercise, setExercise] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null); // { correct: bool, message: '' }

    // Answer state
    const [answerText, setAnswerText] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [showHint, setShowHint] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);

    const loadExercise = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('exercises')
                .select('*')
                .eq('id', id)
                .maybeSingle();

            if (error) throw error;
            if (!data) {
                navigate('/practice');
                return;
            }
            setExercise(data);
        } catch (err) {
            console.error('[ExerciseSolver] Load error:', err);
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        loadExercise();
    }, [loadExercise]);

    const renderedPreview = useMemo(() => {
        return renderLatex(answerText);
    }, [answerText]);

    const renderedFront = useMemo(() => {
        if (!exercise?.front_content) return '';
        return renderLatex(exercise.front_content);
    }, [exercise?.front_content]);

    const renderedHint = useMemo(() => {
        if (!exercise?.hint_content) return '';
        return renderLatex(exercise.hint_content);
    }, [exercise?.hint_content]);

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            setUploadedImage(ev.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async () => {
        if (!exercise || !user) return;
        setSubmitting(true);
        setResult(null);

        try {
            let isCorrect = false;
            let userAnswer = '';

            if (exercise.answer_type === 'multiple_choice' || exercise.answer_type === 'true_false') {
                if (selectedOption === null) {
                    setResult({ correct: false, message: 'Please select an answer.' });
                    setSubmitting(false);
                    return;
                }
                const correctIdx = exercise.options?.findIndex(o => o.isCorrect);
                isCorrect = selectedOption === correctIdx;
                userAnswer = exercise.options?.[selectedOption]?.text || '';
            } else if (exercise.answer_type === 'numeric_input') {
                const userNum = parseFloat(answerText.trim());
                const correctNum = parseFloat(exercise.numeric_answer);
                isCorrect = !isNaN(userNum) && !isNaN(correctNum) && Math.abs(userNum - correctNum) < 0.001;
                userAnswer = answerText.trim();
            } else {
                // free_response — always mark as submitted, needs review
                userAnswer = answerText.trim();
                isCorrect = false; // will be reviewed
            }

            const submissionPayload = {
                user_id: user.id,
                exercise_id: exercise.id,
                answer_text: userAnswer,
                is_correct: isCorrect,
                points: isCorrect ? 3 : 0,
                created_at: new Date().toISOString(),
            };

            // Save submission to database (table may not exist yet / may be RLS-protected)
            let persistedToDb = false;
            try {
                const { error: subErr } = await supabase.from('submissions').insert(submissionPayload);
                if (subErr) {
                    console.debug('[ExerciseSolver] Submission save note:', subErr.message);
                } else {
                    persistedToDb = true;
                }
            } catch (err) {
                // submissions table may not exist yet
            }

            // Always keep a local fallback so dashboard can still show progress
            appendLocalSubmission(user.id, submissionPayload);

            if (exercise.answer_type === 'free_response') {
                setResult({
                    correct: null,
                    message: persistedToDb
                        ? 'Your answer has been submitted and will be reviewed!'
                        : 'Your answer was saved locally. It could not be saved to the database (check RLS/policies).',
                });
            } else {
                setResult({
                    correct: isCorrect,
                    message: isCorrect
                        ? (persistedToDb
                            ? '🎉 Correct! Great job!'
                            : '🎉 Correct! Saved locally, but not to database (check RLS/policies).')
                        : '❌ Not quite right. Try again or check the hint.',
                });
            }
        } catch (err) {
            console.error('[ExerciseSolver] Submit error:', err);
            setResult({ correct: false, message: 'Something went wrong. Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className={`exercise-solver ${isDarkTheme ? 'dark' : 'light'}`}>
                <div className="solver-container">
                    <div className="solver-loading">Loading exercise...</div>
                </div>
            </div>
        );
    }

    if (!exercise) {
        return (
            <div className={`exercise-solver ${isDarkTheme ? 'dark' : 'light'}`}>
                <div className="solver-container">
                    <div className="solver-empty">Exercise not found.</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`exercise-solver ${isDarkTheme ? 'dark' : 'light'}`}>
            <div className="solver-container">
                {/* Back button & title */}
                <div className="solver-top">
                    <button className="solver-back-btn" onClick={() => navigate('/practice')}>
                        ← Back to Practice
                    </button>
                    <h1 className="solver-title">{exercise.title}</h1>
                    {exercise.tags && exercise.tags.length > 0 && (
                        <div className="solver-tags">
                            {exercise.tags.map((tag, i) => (
                                <span key={i} className="solver-tag">{tag}</span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Exercise Statement */}
                <div className="solver-statement card">
                    <h2>Problem</h2>
                    <div
                        className="solver-statement-content"
                        dangerouslySetInnerHTML={{ __html: renderedFront }}
                    />
                    {exercise.front_images && exercise.front_images.length > 0 && (
                        <div className="solver-statement-images">
                            {exercise.front_images.map((url, i) => (
                                <img key={i} src={url} alt={`Figure ${i + 1}`} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Hint Toggle */}
                {exercise.hint_content && (
                    <div className="solver-hint-section">
                        <button
                            className="solver-hint-toggle"
                            onClick={() => setShowHint(!showHint)}
                        >
                            {showHint ? '🔽 Hide Hint' : '💡 Show Hint'}
                        </button>
                        {showHint && (
                            <div className="solver-hint card">
                                <div dangerouslySetInnerHTML={{ __html: renderedHint }} />
                            </div>
                        )}
                    </div>
                )}

                {/* Answer Section */}
                {(exercise.answer_type === 'multiple_choice' || exercise.answer_type === 'true_false') ? (
                    <div className="solver-options card">
                        <h2>Choose your answer</h2>
                        <div className="solver-options-list">
                            {(exercise.options || []).map((opt, index) => (
                                <button
                                    key={index}
                                    className={`solver-option ${selectedOption === index ? 'selected' : ''} ${
                                        result !== null && opt.isCorrect ? 'correct-highlight' : ''
                                    } ${result !== null && selectedOption === index && !opt.isCorrect ? 'wrong-highlight' : ''}`}
                                    onClick={() => { if (!result) setSelectedOption(index); }}
                                    disabled={result?.correct === true}
                                >
                                    <span className="option-letter">
                                        {String.fromCharCode(65 + index)}
                                    </span>
                                    <span
                                        className="option-text"
                                        dangerouslySetInnerHTML={{ __html: renderLatex(opt.text) }}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    /* Free Response / Numeric Input — Preview UI matching screenshot */
                    <div className="solver-answer-section">
                        <div className="solver-answer-grid">
                            <div className="solver-answer-left">
                                <h2>Your Solution</h2>
                                <div className="solver-textarea-wrapper">
                                    <textarea
                                        className="solver-textarea"
                                        value={answerText}
                                        onChange={(e) => setAnswerText(e.target.value)}
                                        placeholder="Write your solution here..."
                                        rows={12}
                                        disabled={result?.correct === true}
                                    />
                                    <div className="solver-textarea-actions">
                                        <button
                                            type="button"
                                            className="solver-action-icon"
                                            title="Upload photo of handwritten work"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                <circle cx="8.5" cy="8.5" r="1.5"/>
                                                <polyline points="21 15 16 10 5 21"/>
                                            </svg>
                                        </button>
                                        <button type="button" className="solver-action-icon" title="Add tags">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                                                <line x1="7" y1="7" x2="7.01" y2="7"/>
                                            </svg>
                                        </button>
                                        <button type="button" className="solver-action-icon" title="Voice input">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                                                <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                                                <line x1="12" y1="19" x2="12" y2="23"/>
                                                <line x1="8" y1="23" x2="16" y2="23"/>
                                            </svg>
                                        </button>
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        style={{ display: 'none' }}
                                        onChange={handleImageUpload}
                                    />
                                </div>
                                <p className="solver-input-hint">
                                    Write in any language you prefer – plain text, LaTeX, or even upload a photo of handwritten work using the camera icon.
                                </p>
                            </div>
                            <div className="solver-answer-right">
                                <h2>Preview</h2>
                                <div className="solver-preview-box">
                                    {answerText || uploadedImage ? (
                                        <>
                                            {answerText && (
                                                <div
                                                    className="solver-preview-content"
                                                    dangerouslySetInnerHTML={{ __html: renderedPreview }}
                                                />
                                            )}
                                            {uploadedImage && (
                                                <div className="solver-preview-image">
                                                    <img src={uploadedImage} alt="Uploaded work" />
                                                    <button
                                                        className="solver-remove-image"
                                                        onClick={() => setUploadedImage(null)}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <span className="solver-preview-placeholder">
                                            Your answer preview will appear here…
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Result Message */}
                {result && (
                    <div className={`solver-result ${result.correct === true ? 'correct' : result.correct === false ? 'wrong' : 'pending'}`}>
                        <span>{result.message}</span>
                    </div>
                )}

                {/* Solution (shown after correct answer) */}
                {result?.correct === true && exercise.back_content && (
                    <div className="solver-solution card">
                        <h2>Solution</h2>
                        <div dangerouslySetInnerHTML={{ __html: renderLatex(exercise.back_content) }} />
                    </div>
                )}

                {/* Submit Button */}
                <div className="solver-submit-row">
                    <button
                        className="solver-submit-btn"
                        onClick={handleSubmit}
                        disabled={submitting || result?.correct === true}
                    >
                        {submitting ? 'Submitting...' : 'Submit Answer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExerciseSolver;
